/**
 * The organization's 3px identity keyline (GH-853, owner 2026-10-02).
 *
 * DESIGN.md: "A tenant's color never becomes the accent. It may tint the 3px identity keyline.
 * Nothing else." Lantern §5 places it at the top of the tenant's page ("tenant color may tint the
 * 3px top identity keyline only. Interactive green never changes."). So the organization's
 * `brandColor` (GH-850) reaches exactly one property — the custom property the keyline reads — and
 * no button, link, focus ring or selection ever sees it.
 *
 * ## No colour, no line
 *
 * Lantern names no neutral token for the identity keyline, and the page already has brass structure
 * where it is needed (the mark's 1px tile keyline). A brass 3px rule on every organization that has
 * not chosen a colour would add an element to hundreds of pages nobody asked to change, and would
 * read as the platform's identity rather than the tenant's. So an organization with no colour gets
 * no keyline, and its page is exactly what it was before this change.
 *
 * ## Only `#rrggbb` reaches the style
 *
 * The value arrives from a public API and lands in a `style` attribute. React escapes attribute
 * text, but a CSS value is its own language — `red;background:url(…)` is harmless HTML and a live
 * declaration. The server's schema and the database CHECK already hold the spelling; this checks it
 * a third time at the last moment, so nothing a compromised or older server sends can become
 * anything but a colour.
 */
import type { CSSProperties } from "react";
import { BRAND_COLOR_PATTERN } from "@givehere/contracts";

/** The custom property the keyline's CSS reads (`.identity-keyline` in styles.css). */
export const IDENTITY_KEYLINE_PROPERTY = "--identity-keyline";

/** The colour to draw, or null when nothing should be drawn. Lower-case `#rrggbb` only. */
export function identityKeylineColor(brandColor: string | null | undefined): string | null {
  return typeof brandColor === "string" && BRAND_COLOR_PATTERN.test(brandColor) ? brandColor : null;
}

/**
 * The class and style that draw the keyline on the page's card, or nothing at all.
 *
 * Spread onto the element: `<main className={`card ${keyline.className}`} style={keyline.style}>`.
 */
export function identityKeyline(brandColor: string | null | undefined): {
  readonly className: string;
  readonly style: CSSProperties | undefined;
} {
  const color = identityKeylineColor(brandColor);
  if (color === null) return { className: "", style: undefined };
  return {
    className: "identity-keyline",
    style: { [IDENTITY_KEYLINE_PROPERTY]: color } as CSSProperties,
  };
}
